import { useEffect } from 'react';
import { GoogleTagManager } from '@next/third-parties/google';
import { useLocalStorageValue } from '@react-hookz/web';
import clsx from 'clsx';
import { LazyMotion } from 'framer-motion';
import { Router, useRouter } from 'next/router';

import {
  Intercom,
  ProductionScripts,
  SixSenseDataProxy,
} from '@/components/common/inline-scripts';
import { PreviewProvider } from '@/components/layout/PreviewProvider';
import {
  addCookieForUTM,
  addUTMToPlatformLinks,
  getCookie,
  GTMPageView,
} from '@/lib/utils';
import { saans } from '@/styles/fonts';

import type { AppProps } from 'next/app';

import '@/styles/global.scss';

const GTM_ID = process.env.NODE_ENV === 'production' ? 'GTM-KWQD2SP' : '';

const isDateOneHourOrMoreInThePast = (date) => {
  const oneHour = 1000 * 60 * 60;
  const oneHourAgo = Date.now() - oneHour;
  return date < oneHourAgo;
};

const loadMotionFeatures = () =>
  import('@/lib/motionFeatures').then((res) => res.default);

const App = ({ Component, pageProps }: AppProps) => {
  const { draftMode, token } = pageProps;
  const router = useRouter();
  const previousIdentityRequestCompletedTime = useLocalStorageValue(
    'previousIdentityRequestCompletedTime',
  );

  useEffect(() => {
    let sixSenseData = null;

    const postIdentifyRequest = async () => {
      window.DY.API('callback', async () => {
        const dyid = getCookie('_dyid');
        if (!dyid?.length) return;
        await fetch('/api/identify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sixSenseData, dyid }),
        });
        previousIdentityRequestCompletedTime.set(Date.now());
      });
    };

    const handleDataLayerPush = () => {
      if (!sixSenseData) {
        Object.entries(window.dataLayer).forEach(([key, entry]) => {
          if (
            Object.prototype.hasOwnProperty.call(window.dataLayer, key) &&
            typeof entry === 'object'
          ) {
            if (
              Object.prototype.hasOwnProperty.call(entry, '6si_company_match')
            ) {
              sixSenseData = entry;
              if (
                !previousIdentityRequestCompletedTime.value ||
                isDateOneHourOrMoreInThePast(
                  previousIdentityRequestCompletedTime.value,
                )
              ) {
                postIdentifyRequest();
              }
            }
          }
        });
      }
    };

    window.addEventListener('dataLayerPush', handleDataLayerPush);

    return () => {
      window.removeEventListener('dataLayerPush', handleDataLayerPush);
    };
  }, [previousIdentityRequestCompletedTime]);

  useEffect(() => {
    const handleRouteChange = (url: string) => GTMPageView(url);

    Router.events.on('routeChangeComplete', handleRouteChange);
    Router.events.on('routeChangeComplete', addUTMToPlatformLinks);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
      Router.events.off('routeChangeComplete', addUTMToPlatformLinks);
    };
  }, []);

  useEffect(() => {
    if (
      router.isReady &&
      Object.keys(router.query).filter((key) => /utm/.test(key)).length > 0
    ) {
      addCookieForUTM(router.query);
      addUTMToPlatformLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  const appContents = (
    <LazyMotion features={loadMotionFeatures} strict>
      <div className={clsx(saans.className)}>
        <Component {...pageProps} />
      </div>
      <GoogleTagManager gtmId={GTM_ID} />
      <ProductionScripts />
      <Intercom />
      <SixSenseDataProxy />
    </LazyMotion>
  );

  if (draftMode) {
    return <PreviewProvider token={token}>{appContents}</PreviewProvider>;
  }

  return appContents;
};

export default App;
